import { apiClient } from './apiClient';

export interface RestaurantInfo {
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  cuisine_type: string | null;
  hours: string | null;
  rating: number | null;
  website: string | null;
}

export interface MenuData {
  source_url: string;
  source_type: 'pdf' | 'image';
  menu_type: string;
  type_confidence: number;
  data: any;
  item_count: number;
  suggested_description: string;
  is_seasonal: boolean;
  has_preview?: boolean;
  file_type?: 'pdf' | 'image';
  thumbnail_url?: string | null;
  user_editable?: {
    menu_type: boolean;
    description: boolean;
    is_seasonal: boolean;
  };
}

export interface SearchCompleteRequest {
  restaurant_name: string;
  location?: string;
  include_images?: boolean;
  max_menus?: number;
  restaurant_data?: any;  // Full restaurant data from Google Places autocomplete
}

export interface SearchCompleteResponse {
  success: boolean;
  restaurant: RestaurantInfo;
  menus: MenuData[];
  search_timestamp: string;
  location_info?: any;
  needs_manual_upload?: boolean;  // New flag for manual upload fallback
  has_location_duplicates?: boolean;
}

export interface MenuTypeInfo {
  type_code: string;
  display_name: string;
  description: string;
  typical_keywords: string[];
}

export interface ExtractMultipleRequest {
  restaurant_name: string;
  menus: Array<{
    source_url: string;
    menu_type: string;
    description: string;
    is_seasonal: boolean;
    seasonal_config?: {
      start_date: string;
      end_date: string;
    };
  }>;
}

export interface ExtractMultipleResponse {
  processing_id?: string;
  status?: string;
  menu_count?: number;
  estimated_time_seconds?: number;
  tasks?: any[];
  // New fields for unified processor response
  success?: boolean;
  processing_complete?: boolean;
  successful_menus?: any[];
  failed_menus?: any[];
  total_items_extracted?: number;
  total_processing_time?: number;
  restaurant_name?: string;
  // Pre-auth flow fields
  session_token?: string;
  requires_auth?: boolean;
  preview_mode?: boolean;
}

export interface MenuTypeDetectionRequest {
  menu_text: string;
  filename?: string;
  metadata?: any;
}

export interface MenuTypeDetectionResponse {
  detected_type: string;
  confidence: number;
  is_seasonal: boolean;
  alternative_types: string[];
  editable: boolean;
}

export interface ExtractionStatusResponse {
  processing_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  overall_progress: number;
  menus: Array<{
    menu_type: string;
    status: string;
    progress: number;
    item_count?: number;
    current_step?: string;
    error?: string;
  }>;
}

class FrictionlessApi {
  private baseUrl = '/frictionless-onboarding';

  /**
   * Search for restaurant and menu sources
   */
  async searchComplete(request: SearchCompleteRequest): Promise<SearchCompleteResponse> {
    const response = await apiClient.post(`${this.baseUrl}/search-complete`, request);
    return response.data;
  }

  /**
   * Extract multiple menus with types (requires auth)
   */
  async extractMultiple(request: ExtractMultipleRequest): Promise<ExtractMultipleResponse> {
    // Extended timeout for menu extraction (5 minutes)
    // Some complex menus with GPT-5 can take 3-4 minutes
    const response = await apiClient.post(`${this.baseUrl}/extract-multiple`, request, {
      timeout: 300000 // 5 minutes in milliseconds
    });
    return response.data;
  }

  /**
   * Extract multiple menus WITHOUT authentication (pre-auth flow)
   * Creates temporary session for frictionless onboarding
   */
  async extractMultiplePreview(request: ExtractMultipleRequest): Promise<ExtractMultipleResponse> {
    // Extended timeout for menu extraction (5 minutes)
    // GPT-5/GPT-4o extraction can take 2-4 minutes for complex menus
    const response = await apiClient.post(`${this.baseUrl}/extract-multiple-preview`, request, {
      timeout: 300000 // 5 minutes in milliseconds
    });
    return response.data;
  }

  /**
   * Claim preview menus after signup/login
   * Transfers temporary menu data to real restaurant account
   */
  async claimPreviewMenus(sessionToken: string): Promise<any> {
    const response = await apiClient.post(`${this.baseUrl}/claim-preview-menus`, { session_token: sessionToken });
    return response.data;
  }

  /**
   * Detect menu type from text
   */
  async detectMenuTypes(request: MenuTypeDetectionRequest): Promise<MenuTypeDetectionResponse> {
    const response = await apiClient.post(`${this.baseUrl}/detect-menu-types`, request);
    return response.data;
  }

  /**
   * Get list of supported menu types
   */
  async getSupportedMenuTypes(): Promise<MenuTypeInfo[]> {
    const response = await apiClient.get(`${this.baseUrl}/supported-menu-types`);
    return response.data;
  }

  /**
   * Get extraction status for batch processing
   */
  async getExtractionStatus(processingId: string): Promise<ExtractionStatusResponse> {
    const response = await apiClient.get(`${this.baseUrl}/extraction-status/${processingId}`);
    return response.data;
  }

  /**
   * Get restaurant autocomplete using Google Places
   */
  async getRestaurantAutocomplete(query: string, location?: string): Promise<any[]> {
    const params = new URLSearchParams({ query });
    if (location) params.append('location', location);
    
    const response = await apiClient.get(`${this.baseUrl}/restaurant-autocomplete?${params}`);
    return response.data;
  }
  
  /**
   * DEPRECATED: Use getRestaurantAutocomplete instead
   * Get restaurant suggestions for autocomplete
   */
  async getRestaurantSuggestions(query: string, location?: string): Promise<any[]> {
    return this.getRestaurantAutocomplete(query, location);
  }

  /**
   * Poll for extraction completion
   */
  async pollExtractionStatus(
    processingId: string,
    onProgress?: (status: ExtractionStatusResponse) => void,
    maxAttempts = 60,
    intervalMs = 2000
  ): Promise<ExtractionStatusResponse> {
    let attempts = 0;
    
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await this.getExtractionStatus(processingId);
          
          if (onProgress) {
            onProgress(status);
          }
          
          if (status.status === 'completed') {
            resolve(status);
          } else if (status.status === 'failed') {
            reject(new Error('Extraction failed'));
          } else if (attempts >= maxAttempts) {
            reject(new Error('Extraction timeout'));
          } else {
            attempts++;
            setTimeout(poll, intervalMs);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      poll();
    });
  }
}

export const frictionlessApi = new FrictionlessApi();